import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useFirebase } from "firebase";
import GrantScreenSkeleton from "pages/Login/GrantScreenSkeleton";
import LoginGrantLogin from "pages/Login/LoginGrantLogin";
import {
  SUPPORT_LINES,
  supportLineKindFor,
} from "pages/Login/login-grant-flow";
import { confirmedDestination } from "pages/StartLead/awaitingConfirmation";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import { leadFlowEvents, type VerifyEntryKind } from "pages/StartLead/events";
import { clearStoredState } from "pages/StartLead/state";
import type { ConfirmRefusal } from "pages/StartLead/submissionStatus";
import { useStatusClient } from "pages/StartLead/useStatusClient";
import { Frame } from "pages/QuoteFlow/CaseQuoteFrame";

import LookUp from "./LookUp";
import Pending from "./Pending";
import Refused from "./Refused";
import { useCorrector } from "./useCorrector";
import {
  pendingRetryDelayMs,
  readVerifyNotFound,
  resolveVerify,
  verifyConfirmedEvent,
  verifyEntryFor,
  verifyPendingEventFor,
  verifyRedirectFor,
  verifyScreenOf,
  verifyScreenToReport,
  type VerifyEntry,
} from "./verify";

type Phase =
  | { kind: "checking" }
  | { kind: "ready" }
  | { kind: "look_up" }
  | { kind: "refused"; reason: ConfirmRefusal }
  /** `retryInMs` is null once the attempts are used up. */
  | { kind: "pending"; retryInMs: number | null };

const CHECKING: Phase = { kind: "checking" };

/**
 * The code screen for a lead submission. Once the visitor's sign-in is
 * known, a visitor signed in as the submission's own person is confirmed and
 * goes on to its quote, on every visit, reloads included, or is told the
 * request expired or closed, or waits while it is finished. Otherwise the screen runs on the grant the lead
 * form handed over in router state, or mints a grant for the submission in
 * `?s=` (which a remembered browser also confirms). A visitor who cannot be
 * given a grant looks themselves up with the email or phone they used, and
 * the page checks again once they are signed in.
 */
export default function StartVerify() {
  const { search, state } = useLocation();
  const navigate = useNavigate();
  const trackEvent = useTrackLeadFlowEvent();
  const [phase, setPhase] = useState<Phase>(CHECKING);
  const [attempt, setAttempt] = useState(0);
  const startedFor = useRef<string | null>(null);
  const { user, loading, logout } = useFirebase();
  const userRef = useRef(user);
  userRef.current = user;
  const signedIn = user !== null;
  const pendingAnswers = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const notFoundFromLink = useRef(readVerifyNotFound(state));
  const entry = verifyEntryFor(state);
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const grantDiscarded = useRef(false);
  const retriedPending = useRef(false);
  const viewEntry = useRef<VerifyEntryKind>(entry.kind);
  const client = useStatusClient();

  useEffect(() => {
    if (!signedIn || phaseRef.current.kind !== "look_up") return;
    if (verifyRedirectFor(search) === undefined) {
      navigate("/start", { replace: true });
      return;
    }
    setPhase(CHECKING);
    setAttempt((count) => count + 1);
    // Only a change of sign-in re-checks; the URL is read as it stands then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  useEffect(() => {
    if (phase.kind !== "pending" || phase.retryInMs === null) return;
    const timer = setTimeout(
      () => setAttempt((count) => count + 1),
      phase.retryInMs,
    );
    return () => clearTimeout(timer);
  }, [phase]);

  const reportedRefusal = useRef<Phase | null>(null);
  useEffect(() => {
    if (phase.kind !== "refused" || reportedRefusal.current === phase) return;
    reportedRefusal.current = phase;
    trackEvent(leadFlowEvents.verifyRefused(phase.reason));
  }, [phase, trackEvent]);

  const reportedScreen = useRef<ReturnType<typeof verifyScreenOf>>(null);
  useEffect(() => {
    const screen = verifyScreenOf(phase.kind);
    const toReport = verifyScreenToReport(reportedScreen.current, screen);
    reportedScreen.current = screen;
    if (toReport === null) return;
    trackEvent(
      leadFlowEvents.verifyViewed({
        screen: toReport,
        entry: viewEntry.current,
      }),
    );
  }, [phase.kind, trackEvent]);

  useEffect(() => {
    const key = `${search}#${attempt}`;
    if (loading || startedFor.current === key) return;
    startedFor.current = key;
    if (notFoundFromLink.current) {
      notFoundFromLink.current = false;
      setPhase({ kind: "refused", reason: "not_found" });
      return;
    }
    const answeredBefore = pendingAnswers.current;
    const startedFrom: VerifyEntry = grantDiscarded.current
      ? { kind: "mint" }
      : entryRef.current;
    void resolveVerify(
      search,
      startedFrom,
      userRef.current !== null,
      client,
      answeredBefore === 0 && !retriedPending.current ? trackEvent : undefined,
    ).then((resolution) => {
      const pendingEvent = verifyPendingEventFor(answeredBefore, resolution);
      if (pendingEvent !== null) trackEvent(pendingEvent);
      viewEntry.current =
        resolution.kind === "login" ? "mint" : startedFrom.kind;
      if (resolution.kind === "confirmed") {
        trackEvent(verifyConfirmedEvent(resolution.via));
        clearStoredState();
        navigate(
          confirmedDestination(resolution.next, resolution.submissionId),
          { replace: true },
        );
        return;
      }
      if (resolution.kind === "login") {
        navigate(resolution.path, { replace: true, state: resolution.state });
        setPhase({ kind: "ready" });
        return;
      }
      if (resolution.kind === "pending") {
        pendingAnswers.current += 1;
        setPhase({
          kind: "pending",
          retryInMs: pendingRetryDelayMs(pendingAnswers.current),
        });
        return;
      }
      pendingAnswers.current = 0;
      if (resolution.kind === "grant") setPhase({ kind: "ready" });
      else setPhase(resolution);
    });
  }, [search, attempt, loading, client, navigate, trackEvent]);

  const signOutForLookUp = async () => {
    trackEvent(leadFlowEvents.useDifferentClicked("verify"));
    await logout();
    setPhase({ kind: "look_up" });
  };

  const checkAgain = () => {
    retriedPending.current = true;
    pendingAnswers.current = 0;
    setPhase(CHECKING);
    setAttempt((count) => count + 1);
  };

  const checkWithoutGrant = () => {
    grantDiscarded.current = true;
    pendingAnswers.current = 0;
    navigate({ search }, { replace: true, state: null });
    setPhase(CHECKING);
    setAttempt((count) => count + 1);
  };

  // One number for the header and the screen: the pre-need line only when the
  // grant says the lead is planning ahead, the first-call line otherwise.
  const supportKind = supportLineKindFor(
    entry.kind === "grant" ? entry.grant.leadKind : undefined,
  );
  const line = SUPPORT_LINES[supportKind];

  const { correct, typed } = useCorrector(search);

  if (phase.kind === "ready" && entry.kind === "grant") {
    return (
      <LoginGrantLogin
        grant={entry.grant}
        surface="verify"
        onUnrenewable={checkWithoutGrant}
        onCorrect={correct}
        typed={typed}
      />
    );
  }
  if (phase.kind === "look_up") {
    return (
      <Frame shell="standalone" line={line}>
        <LookUp
          redirect={verifyRedirectFor(search)}
          line={line}
          onNotFound={() => setPhase({ kind: "refused", reason: "not_found" })}
        />
      </Frame>
    );
  }
  if (phase.kind === "pending") {
    return (
      <Frame shell="standalone" line={line}>
        <Pending
          retrying={phase.retryInMs !== null}
          line={line}
          onRetry={checkAgain}
        />
      </Frame>
    );
  }
  if (phase.kind === "refused") {
    return (
      <Frame shell="standalone" line={line}>
        <Refused
          reason={phase.reason}
          line={line}
          onUseDifferent={() => void signOutForLookUp()}
        />
      </Frame>
    );
  }
  return (
    <GrantScreenSkeleton
      supportKind={supportKind}
      surface="verify"
      withFix={correct !== undefined}
    />
  );
}
